import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import takaIcon from 'assets/taka-logo-icon.svg'
import { useAnalytics, useIsMobile } from 'hooks'
import { X } from 'lucide-react'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { OPEN_ASSISTANT_CHAT_EVENT } from '../assistant-chat-events'
import { dockSurfaceForPath } from '../surfaces'
import { useAssistantChatUIStore } from '../store/assistant-chat-ui-store'
import { AssistantChat } from './AssistantChat'
import { MobileBottomSheet } from './MobileBottomSheet'

/** Screen-specific "Try asking" chips — never open an empty chat. */
const STARTER_PROMPTS: Record<string, string[]> = {
  calendar: [
    "What's going out this week?",
    'What published in the last few days?',
    'Is my weekly plan running?',
    'Draft a post for an empty day',
  ],
  posts: [
    "What's scheduled this week?",
    'How are my recent posts doing?',
    'Create a post about a new offer',
  ],
  brand: [
    'What do you know about my brand?',
    'Make our tone more playful',
    'Add a rule: never use corporate buzzwords',
    'Update our brand colors',
  ],
  integrations: [
    'Which of my accounts are connected?',
    'What platforms can Taka post to?',
    "What's my weekly plan set up to do?",
  ],
  billing: ['How many credits do I have left?', 'What do credits get used for?'],
  collaborators: [
    "What's going out this week?",
    'Which of my accounts are connected?',
    'Draft a post about a new offer',
  ],
  profile: [
    "What's going out this week?",
    'How many credits do I have left?',
    'How are my recent posts doing?',
  ],
}

/**
 * The assistant, summonable on every app screen — same colleague, same
 * thread, same brain as the home assistant. Renders the docked pill and a
 * right-side panel (desktop) or a bottom sheet (mobile).
 */
export function AssistantDock() {
  const location = useLocation()
  const isMobile = useIsMobile()
  const { track } = useAnalytics()
  const open = useAssistantChatUIStore(s => s.open)
  const setOpen = useAssistantChatUIStore(s => s.setOpen)

  const surface = dockSurfaceForPath(location.pathname)

  // cmd-K "Ask Taka" and other app affordances summon the assistant by event.
  useEffect(() => {
    if (!surface) return
    const handleOpen = () => setOpen(true)
    window.addEventListener(OPEN_ASSISTANT_CHAT_EVENT, handleOpen)
    return () => window.removeEventListener(OPEN_ASSISTANT_CHAT_EVENT, handleOpen)
  }, [surface, setOpen])

  if (!surface) return null

  const handleOpenClick = () => {
    setOpen(true)
    track(AnalyticsEvents.AssistantChat.ASSISTANT_CHAT_OPENED, {
      platform: isMobile ? 'mobile' : 'desktop',
      source: 'global_dock',
      surface,
    })
  }

  if (isMobile) {
    return (
      <>
        {!open && (
          <button
            type="button"
            onClick={handleOpenClick}
            aria-label="Taka Agent"
            className="fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-40 inline-flex items-center gap-2 rounded-2xl rounded-br-sm border border-line-bright bg-bg-2 py-2 pr-3 pl-2.5 text-paper shadow-lg transition-colors hover:border-acid/40 hover:bg-bg-3"
          >
            <img
              src={takaIcon}
              alt=""
              className="h-6 w-6 rounded-[5px] shadow-[0_0_12px_var(--acid-soft)]"
            />
            <span className="text-[13px] font-medium text-paper">Taka Agent</span>
          </button>
        )}
        <MobileBottomSheet open={open} onClose={() => setOpen(false)} title="Taka Agent">
          {/* Only mount the chat once it's actually open: the sheet portals its
              children even while closed, and a hidden mount fetches the whole
              assistant history on every page load AND steals focus to an
              offscreen textarea (the deleted BrandKitChatDrawer documented
              this exact trap). */}
          {open && (
            <AssistantChat
              showHeader={false}
              surface={surface}
              starterPrompts={STARTER_PROMPTS[surface]}
            />
          )}
        </MobileBottomSheet>
      </>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpenClick}
        aria-label="Taka Agent"
        className="fixed right-6 bottom-6 z-40 inline-flex items-center gap-2 rounded-2xl rounded-br-sm border border-line-bright bg-bg-2 py-2 pr-3 pl-2.5 text-paper shadow-lg transition-colors hover:border-acid/40 hover:bg-bg-3"
      >
        <img
          src={takaIcon}
          alt=""
          className="h-6 w-6 rounded-[5px] shadow-[0_0_12px_var(--acid-soft)]"
        />
        <span className="text-[13px] font-medium text-paper">Taka Agent</span>
      </button>
    )
  }

  return (
    <div className="fixed right-4 bottom-4 z-40 flex h-[min(640px,calc(100vh-5rem))] w-[400px] flex-col overflow-hidden rounded-[12px] shadow-2xl">
      <div className="flex h-full flex-col overflow-hidden rounded-[12px] border border-line bg-bg-2">
        <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
          <div className="flex items-center gap-2">
            <img src={takaIcon} alt="" className="h-5 w-5 rounded-[4px]" />
            <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">Taka Agent</h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close chat"
            className="-mr-1 rounded-[6px] p-1 text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
          >
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1">
          <AssistantChat
            showHeader={false}
            surface={surface}
            starterPrompts={STARTER_PROMPTS[surface]}
          />
        </div>
      </div>
    </div>
  )
}
